(() => {
  const initialiseCollection = section => {
    if (!section || section.dataset.collectionReady === 'true') return;
    section.dataset.collectionReady = 'true';
    const form = section.querySelector('[data-facet-form]');
    const drawer = section.querySelector('[data-filter-drawer]');
    const openButton = section.querySelector('[data-filter-open]');
    let rowBufferFrame = 0;

    const getProductCards = grid => {
      const seenHandles = new Set();

      return [...grid.children].filter(card => {
        if (!card.matches('[data-tazty-product-card]')) return false;

        const handle = card.dataset.productHandle;
        if (handle && seenHandles.has(handle)) {
          card.remove();
          return false;
        }
        if (handle) seenHandles.add(handle);
        return true;
      });
    };

    const getTotalProducts = () => {
      const total = section.querySelector('[data-result-total]');
      return Number(total?.dataset.totalProducts || total?.textContent.replace(/[^0-9]/g, '')) || 0;
    };

    const syncCompleteRows = () => {
      const grid = section.querySelector('[data-product-grid]');
      if (!grid) return;
      const cards = getProductCards(grid);
      cards.forEach(card => {
        card.classList.remove('is-pagination-buffered');
        card.removeAttribute('aria-hidden');
      });

      const hasMoreProducts = Boolean(section.querySelector('[data-load-more]'));
      const totalProducts = getTotalProducts();
      let visibleCards = totalProducts > 0 ? Math.min(cards.length, totalProducts) : cards.length;
      if (hasMoreProducts && cards.length) {
        const columns = Math.max(1, getComputedStyle(grid).gridTemplateColumns.split(/\s+/).filter(Boolean).length);
        const incompleteRowSize = cards.length % columns;
        if (incompleteRowSize) {
          cards.slice(-incompleteRowSize).forEach(card => {
            card.classList.add('is-pagination-buffered');
            card.setAttribute('aria-hidden', 'true');
          });
          visibleCards -= incompleteRowSize;
        }
      }

      const visibleCount = section.querySelector('[data-visible-count]');
      if (visibleCount) visibleCount.textContent = String(visibleCards);
      const track = section.querySelector('.tazty-load-more__track span');
      if (track && totalProducts > 0) track.style.width = `${Math.min(100, visibleCards * 100 / totalProducts)}%`;
    };

    const scheduleCompleteRows = () => {
      cancelAnimationFrame(rowBufferFrame);
      rowBufferFrame = requestAnimationFrame(syncCompleteRows);
    };

    const closeFilters = () => {
      section.classList.remove('is-filter-open');
      openButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
    };

    openButton?.addEventListener('click', () => {
      section.classList.add('is-filter-open');
      openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      drawer?.querySelector('button')?.focus();
    });
    section.querySelectorAll('[data-filter-close]').forEach(button => button.addEventListener('click', closeFilters));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && section.classList.contains('is-filter-open')) closeFilters();
    });

    section.querySelectorAll('[data-filter-input]').forEach(input => {
      input.addEventListener('change', () => {
        if (input.name === 'sort_by') {
          section.querySelectorAll('select[name="sort_by"]').forEach(select => { select.value = input.value; });
        }
        if ((input.matches('[data-price-range]') || input.type === 'checkbox') && window.matchMedia('(max-width: 760px)').matches) return;
        (input.form || form)?.requestSubmit();
      });
    });

    section.querySelectorAll('[data-price-range]').forEach(range => {
      const output = range.closest('.tazty-price-filter')?.querySelector('[data-price-output]');
      range.addEventListener('input', () => {
        const currency = window.Shopify?.currency?.active || 'USD';
        try {
          output.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(range.value));
        } catch (_) {
          output.textContent = `${currency} ${range.value}`;
        }
      });
    });

    section.querySelectorAll('[data-grid-view]').forEach(button => {
      button.addEventListener('click', () => {
        const compact = button.dataset.gridView === 'compact';
        section.querySelector('[data-product-grid]')?.classList.toggle('is-compact', compact);
        section.querySelectorAll('[data-grid-view]').forEach(control => {
          const active = control === button;
          control.classList.toggle('is-active', active);
          control.setAttribute('aria-pressed', String(active));
        });
        scheduleCompleteRows();
      });
    });

    section.addEventListener('click', async event => {
      const link = event.target.closest('[data-load-more]');
      if (!link) return;
      event.preventDefault();
      if (link.classList.contains('is-loading') || link.getAttribute('aria-busy') === 'true') return;
      link.classList.add('is-loading');
      link.setAttribute('aria-busy', 'true');
      const originalText = link.textContent;
      link.textContent = 'Loading…';
      try {
        const response = await fetch(link.href, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Unable to load more products.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = html.querySelector(`[data-section-id="${section.dataset.sectionId}"]`);
        const currentGrid = section.querySelector('[data-product-grid]');
        const nextGrid = nextSection?.querySelector('[data-product-grid]');
        if (!currentGrid || !nextGrid) throw new Error('Unable to find more products.');
        const existingHandles = new Set(
          [...currentGrid.querySelectorAll('[data-tazty-product-card][data-product-handle]')]
            .map(card => card.dataset.productHandle)
            .filter(Boolean)
        );
        [...nextGrid.children]
          .filter(card => card.matches('[data-tazty-product-card]'))
          .forEach(card => {
            const handle = card.dataset.productHandle;
            if (handle && existingHandles.has(handle)) return;
            if (handle) existingHandles.add(handle);
            currentGrid.appendChild(card);
          });
        currentGrid.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        const nextLink = nextSection.querySelector('[data-load-more]');
        if (nextLink) {
          link.href = nextLink.href;
          link.textContent = originalText;
          link.classList.remove('is-loading');
          link.removeAttribute('aria-busy');
        } else {
          link.remove();
        }
        syncCompleteRows();
      } catch (_) {
        window.location.assign(link.href);
      }
    });

    window.addEventListener('resize', scheduleCompleteRows, { passive: true });
    scheduleCompleteRows();
  };

  document.querySelectorAll('[data-tazty-collection-section]').forEach(initialiseCollection);
  document.addEventListener('shopify:section:load', event => {
    const section = event.target.matches?.('[data-tazty-collection-section]') ? event.target : event.target.querySelector?.('[data-tazty-collection-section]');
    if (section) initialiseCollection(section);
  });
})();
